export const MAIL_BODY_WARMING_EVENT = "mailBodiesWarming";

export type MailBodyWarmingDetail = {
  accountId: number;
  folder: string;
  uids: number[];
};

const STALE_WARMING_MS = 210_000;

const warmingByFolder = new Map<string, Set<number>>();
const warmingSinceByFolder = new Map<string, Map<number, number>>();
const listeners = new Set<() => void>();

function folderKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function notify(): void {
  listeners.forEach((cb) => cb());
}

function pruneStaleWarming(key: string): void {
  const set = warmingByFolder.get(key);
  if (!set || set.size === 0) return;

  const sinceMap = warmingSinceByFolder.get(key);
  const now = Date.now();
  let changed = false;

  for (const uid of [...set]) {
    const since = sinceMap?.get(uid) ?? 0;
    if (since > 0 && now - since > STALE_WARMING_MS) {
      set.delete(uid);
      sinceMap?.delete(uid);
      changed = true;
    }
  }

  if (set.size === 0) {
    warmingByFolder.delete(key);
    warmingSinceByFolder.delete(key);
    changed = true;
  }

  if (changed) {
    notify();
  }
}

export type MessageBodyCacheStatus = "hydrated" | "ready" | "warming" | "idle";

/**
 * Pastille liste :
 * - hydrated = corps hydraté client (mémoire ou knownReady)
 * - ready = `.eml` sur DD02 seulement (simple coche)
 * - warming = warm **actif** pour cet UID seulement
 * - idle = ni client ni serveur (nuage)
 */
export function resolveMessageBodyCacheStatus(input: {
  fromList: boolean;
  warmingNow: boolean;
  /** Corps hydraté client (mémoire / knownReady). */
  clientCached: boolean;
  serverCached: boolean;
}): MessageBodyCacheStatus {
  const { warmingNow, clientCached, serverCached, fromList } = input;
  const serverOk = serverCached || fromList;

  if (clientCached) {
    return "hydrated";
  }
  if (serverOk) {
    return "ready";
  }
  if (warmingNow) {
    return "warming";
  }
  return "idle";
}

export function subscribeBodyWarmState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isBodyWarming(accountId: number, folder: string, uid: number): boolean {
  if (uid <= 0) return false;
  const key = folderKey(accountId, folder);
  pruneStaleWarming(key);
  return warmingByFolder.get(key)?.has(uid) ?? false;
}

export function getWarmingUids(accountId: number, folder: string): ReadonlySet<number> {
  const key = folderKey(accountId, folder);
  pruneStaleWarming(key);
  return warmingByFolder.get(key) ?? new Set();
}

export function markBodiesWarming(
  accountId: number,
  folder: string,
  uids: number[]
): void {
  const unique = [...new Set(uids.filter((uid) => uid > 0))];
  if (unique.length === 0) return;

  const key = folderKey(accountId, folder);
  let set = warmingByFolder.get(key);
  if (!set) {
    set = new Set();
    warmingByFolder.set(key, set);
  }
  let sinceMap = warmingSinceByFolder.get(key);
  if (!sinceMap) {
    sinceMap = new Map();
    warmingSinceByFolder.set(key, sinceMap);
  }
  const now = Date.now();
  for (const uid of unique) {
    set.add(uid);
    sinceMap.set(uid, now);
  }

  window.dispatchEvent(
    new CustomEvent<MailBodyWarmingDetail>(MAIL_BODY_WARMING_EVENT, {
      detail: { accountId, folder, uids: unique },
    })
  );
  notify();
}

export function clearBodiesWarming(
  accountId: number,
  folder: string,
  uids?: number[]
): void {
  const key = folderKey(accountId, folder);
  const set = warmingByFolder.get(key);
  if (!set) return;

  const sinceMap = warmingSinceByFolder.get(key);

  if (uids == null) {
    warmingByFolder.delete(key);
    warmingSinceByFolder.delete(key);
  } else {
    for (const uid of uids) {
      set.delete(uid);
      sinceMap?.delete(uid);
    }
    if (set.size === 0) {
      warmingByFolder.delete(key);
      warmingSinceByFolder.delete(key);
    }
  }
  notify();
}

export function clearAccountBodiesWarming(accountId: number): void {
  const prefix = `${accountId}\0`;
  for (const key of [...warmingByFolder.keys()]) {
    if (key.startsWith(prefix)) {
      warmingByFolder.delete(key);
      warmingSinceByFolder.delete(key);
    }
  }
  notify();
}

export function clearAllBodiesWarming(): void {
  warmingByFolder.clear();
  warmingSinceByFolder.clear();
  notify();
}
